import { useState, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import appConfig from '../../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;

export interface FinancialParam {
    id: number;
    param_name: string;
    param_value: number;
    created_at: string;
    created_by: string;
}

export interface OwnerParams {
    owner_name: string;
    params: FinancialParam[];
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-api-key': API_KEY || '',
    };
};

export const useFinancialParams = () => {
    const [owners, setOwners] = useState<OwnerParams[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchParams = useCallback(async () => {
        setIsLoading(true);
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/params`, { headers });
            if (!response.ok) throw new Error('Erro ao carregar parâmetros');
            const json = await response.json();
            setOwners(json.data || []);
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Erro ao carregar';
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const updateParam = async (id: number, paramValue: number): Promise<boolean> => {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/params/${id}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ param_value: paramValue }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || 'Erro ao atualizar parâmetro');
                return false;
            }
            toast.success('Parâmetro atualizado com sucesso');
            return true;
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Erro ao conectar';
            toast.error(msg);
            return false;
        }
    };

    const createParam = async (ownerName: string, paramName: string, paramValue: number): Promise<boolean> => {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_URL}/params`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    owner_name: ownerName.toUpperCase(),
                    param_name: paramName.toLowerCase().replace(/\s+/g, '_'),
                    param_value: paramValue,
                }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.message || 'Erro ao criar parâmetro');
                return false;
            }
            toast.success('Parâmetro criado com sucesso');
            return true;
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Erro ao conectar';
            toast.error(msg);
            return false;
        }
    };

    return { owners, isLoading, fetchParams, updateParam, createParam };
};
